"use client";

import Link from "next/link";
import { BookOpen, AlertCircle, Lock, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n/I18nProvider";

interface Section {
  icon: React.ReactNode;
  titleKey: string;
  contentKey: string;
  items?: string[];
}

export default function TermsPage() {
  const { t } = useI18n();

  const sections: Section[] = [
    {
      icon: <BookOpen size={24} className="text-keiro-green" />,
      titleKey: "pages.legal.terms.section.agreement.title",
      contentKey: "pages.legal.terms.section.agreement.content",
    },
    {
      icon: <AlertCircle size={24} className="text-keiro-green" />,
      titleKey: "pages.legal.terms.section.liability.title",
      contentKey: "pages.legal.terms.section.liability.content",
    },
    {
      icon: <Lock size={24} className="text-keiro-green" />,
      titleKey: "pages.legal.terms.section.userResponsibilities.title",
      contentKey: "pages.legal.terms.section.userResponsibilities.content",
      items: [
        "pages.legal.terms.section.userResponsibilities.item1",
        "pages.legal.terms.section.userResponsibilities.item2",
        "pages.legal.terms.section.userResponsibilities.item3",
        "pages.legal.terms.section.userResponsibilities.item4",
      ],
    },
    {
      icon: <Zap size={24} className="text-keiro-green" />,
      titleKey: "pages.legal.terms.section.suspension.title",
      contentKey: "pages.legal.terms.section.suspension.content",
    },
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            {t("pages.legal.terms.title") || "Условия использования"}
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            {t("pages.legal.terms.subtitle") ||
              "Основные правила и соглашение об использовании платформы Keiront"}
          </p>
          <p className="text-sm text-slate-500 mt-4">
            {t("pages.legal.terms.lastUpdated") ||
              "Последнее обновление: 12 апреля 2026"}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-slate-200">
        <p className="text-lg text-slate-600 leading-relaxed mb-6">
          {t("pages.legal.terms.intro") ||
            `Добро пожаловать на Keiront — облачную платформу финансового интеллекта для гостиничного бизнеса.
            Используя наше приложение и услуги, вы соглашаетесь с настоящими Условиями использования.`}
        </p>
        <p className="text-lg text-slate-600 leading-relaxed">
          {t("pages.legal.terms.intro2") ||
            `Если вы не согласны с любой частью этих условий, пожалуйста, не используйте платформу.`}
        </p>
      </section>

      {/* Sections */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
        {sections.map((section, idx) => (
          <section key={idx} className="border-t border-slate-200 pt-12">
            <div className="flex gap-4 mb-6">
              <div className="flex-shrink-0 mt-1">{section.icon}</div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">
                  {t(section.titleKey) || section.titleKey}
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  {t(section.contentKey) || section.contentKey}
                </p>

                {section.items && (
                  <ul className="space-y-3 ml-4">
                    {section.items.map((item, itemIdx) => (
                      <li
                        key={itemIdx}
                        className="flex gap-3 text-slate-600 leading-relaxed"
                      >
                        <span className="text-keiro-green font-bold flex-shrink-0">
                          •
                        </span>
                        <span>{t(item) || item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>
        ))}

        {/* Intellectual Property Section */}
        <section className="border-t border-slate-200 pt-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            {t("pages.legal.terms.ip.title") || "Интеллектуальная собственность"}
          </h2>
          <div className="space-y-4 text-slate-600">
            <p className="leading-relaxed">
              {t("pages.legal.terms.ip.content1") ||
                "Все содержимое платформы Keiront, включая текст, графику, логотипы, исходный код и программное обеспечение, является собственностью ООО 'Кейронт' и защищено законодательством об авторском праве."}
            </p>
            <p className="leading-relaxed">
              {t("pages.legal.terms.ip.content2") ||
                "Вам предоставляется ограниченная, неисключительная лицензия на использование платформы только в целях использования услуг согласно этому соглашению."}
            </p>
          </div>
        </section>

        {/* Data Ownership Section */}
        <section className="border-t border-slate-200 pt-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            {t("pages.legal.terms.dataOwnership.title") ||
              "Право собственности на данные"}
          </h2>
          <div className="space-y-4 text-slate-600">
            <p className="leading-relaxed">
              {t("pages.legal.terms.dataOwnership.content") ||
                "Вы сохраняете полное право собственности на все данные, которые вы загружаете или создаёте в Keiront. Мы используем ваши данные только для предоставления услуг и в соответствии с нашей Политикой конфиденциальности."}
            </p>
          </div>
        </section>

        {/* Service Availability Section */}
        <section className="border-t border-slate-200 pt-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            {t("pages.legal.terms.availability.title") ||
              "Доступность услуги"}
          </h2>
          <div className="space-y-4 text-slate-600">
            <p className="leading-relaxed">
              {t("pages.legal.terms.availability.content1") ||
                "Мы стремимся обеспечить доступность платформы 24/7, но не гарантируем беспрерывное обслуживание."}
            </p>
            <p className="leading-relaxed">
              {t("pages.legal.terms.availability.content2") ||
                "Плановое обслуживание может проводиться без предварительного уведомления, но мы сообщим вам, когда это возможно."}
            </p>
          </div>
        </section>

        {/* Payment Terms Section */}
        <section className="border-t border-slate-200 pt-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            {t("pages.legal.terms.payment.title") || "Условия оплаты"}
          </h2>
          <div className="space-y-4 text-slate-600">
            <p className="leading-relaxed">
              {t("pages.legal.terms.payment.content1") ||
                "Планы и цены зависят от выбранного пакета. Стоимость указывается в рублях и зависит от функциональности."}
            </p>
            <p className="leading-relaxed">
              {t("pages.legal.terms.payment.content2") ||
                "Бесплатная пробная версия доступна в течение 14 дней. После этого требуется оплачиваемый подписка."}
            </p>
            <p className="leading-relaxed">
              {t("pages.legal.terms.payment.content3") ||
                "Платежи обрабатываются в защищённой среде с использованием стандартных способов оплаты."}
            </p>
          </div>
        </section>

        {/* Governing Law Section */}
        <section className="border-t border-slate-200 pt-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            {t("pages.legal.terms.governing.title") ||
              "Применимое законодательство"}
          </h2>
          <div className="space-y-4 text-slate-600">
            <p className="leading-relaxed">
              {t("pages.legal.terms.governing.content") ||
                "Настоящие Условия использования и все связанные с ними соглашения регулируются и толкуются в соответствии с законодательством Российской Федерации."}
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="border-t border-slate-200 pt-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            {t("pages.legal.terms.contact.title") ||
              "Контакты для предложений и жалоб"}
          </h2>
          <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
            <p className="text-slate-600 mb-4">
              {t("pages.legal.terms.contact.desc") ||
                "Если у вас есть вопросы или предложения по нашим Условиям использования:"}
            </p>
            <div className="space-y-2 font-mono text-sm text-slate-900">
              <p>Email: support@keiront.io</p>
              <p>
                {t("pages.legal.terms.contact.address") ||
                  "Адрес: г. Москва, Российская Федерация"}
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-keiro-mist rounded-xl p-12 text-center border border-keiro-green/30">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            {t("pages.legal.terms.cta.title") ||
              "Готовы использовать Keiront?"}
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            {t("pages.legal.terms.cta.desc") ||
              "Зарегистрируйтесь сегодня и начните использовать платформу"}
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button asChild variant="default" className="h-12 px-8">
              <Link href="/register">
                {t("pages.legal.terms.cta.register") || "Зарегистрироваться"}
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-8">
              <Link href="/legal/privacy">
                {t("pages.legal.terms.cta.privacy") ||
                  "Политика конфиденциальности"}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
